import { ReactNode, useRef } from 'react'

import {
  Box,
  Divider,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItem as MuiListItem,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material'
import { AutomationTagIds } from 'assets/test/test-automation-tag-id'
import { EVENT_KEYS } from 'utils/constants'
import { isRtl } from 'utils/i18n'

type Props = {
  title?: string | ReactNode
  description?: string | ReactNode
  icon?: string | ReactNode
  onClick?: () => void
  secondaryAction?: ReactNode
  secondaryIcon?: string | ReactNode
  index?: number
  fullWidth?: boolean
  hasUnderDivider?: boolean
  sx?: SxProps
  iconSx?: SxProps
  listItemSx?: SxProps
  dividerSx?: SxProps
  titleSx?: SxProps
  iconItemSx?: SxProps
  stackSx?: SxProps
  secondaryIconSx?: SxProps
  descriptionBoxSx?: SxProps
  descriptionSx?: SxProps
  secondaryActionSx?: SxProps
}

//TODO: Need to modify HTML dom structure
function ListItem({
  title,
  description,
  icon,
  onClick,
  secondaryAction,
  secondaryIcon,
  index,
  fullWidth,
  hasUnderDivider,
  sx,
  iconSx,
  listItemSx,
  dividerSx,
  titleSx,
  iconItemSx,
  stackSx,
  secondaryIconSx,
  descriptionBoxSx,
  descriptionSx,
  secondaryActionSx
}: Props) {
  const isDownLg = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const isDownMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const actionRef = useRef<any>(null)
  return (
    <Stack
      sx={{
        width: fullWidth
          ? '100%'
          : {
              xs: '100%',
              lg: '450px'
            },
        ...sx
      }}
    >
      {index !== undefined && ((isDownLg && index !== 0) || (!isDownLg && index > 1) || (fullWidth && index !== 0)) && (
        <Divider
          component='li'
          sx={{
            marginTop: '14px',
            marginBottom: '14px',
            marginLeft: !isRtl && icon !== undefined ? '64px' : '0px',
            marginRight: isRtl && icon !== undefined ? '64px' : '0px',
            ...dividerSx
          }}
        />
      )}
      <MuiListItem
        alignItems='center'
        sx={{
          minHeight: { xs: '42px', md: '48px ' },
          padding: '0px',
          cursor: onClick ? 'pointer' : 'inherit',
          userSelect: onClick ? 'none' : 'inherit',
          textAlign: 'start',
          ...listItemSx
        }}
        tabIndex={onClick ? 0 : -1}
        onClick={onClick}
        onKeyDown={(event) => {
          if (onClick && (event.key === EVENT_KEYS.ENTER || event.key === EVENT_KEYS.SPACE)) {
            event.preventDefault()
            onClick()
          }
        }}
        data-testid='MuiListItem6D5173A7'
      >
        {icon !== undefined && (
          <ListItemIcon
            sx={{ margin: '0px', minWidth: '64px', '& a': { cursor: 'default' }, ...iconItemSx }}
            onClick={(event) => {
              event.preventDefault()
            }}
            data-testid='ListItemIcon922C9B4D'
          >
            {typeof icon === 'string' ? (
              <Box component='img' src={icon} sx={{ width: '48px', height: '48px', ...iconSx }} alt='' />
            ) : (
              icon
            )}
          </ListItemIcon>
        )}
        <Stack direction='column' justifyContent='center' sx={{ width: '100%', ...stackSx }}>
          {title !== undefined && (
            <Box sx={{ width: '100%', minHeight: '24px', ...titleSx }}>
              {typeof title === 'string' ? (
                <Typography variant='listItemTitle' data-testid={AutomationTagIds['list.heading'] + '-' + title}>
                  {title}
                </Typography>
              ) : (
                title
              )}
            </Box>
          )}
          {description !== undefined && (
            <Box sx={{ width: '100%', minHeight: { xs: '16px', lg: '22px' }, ...descriptionBoxSx }}>
              {typeof description === 'string' ? (
                <Typography variant='listItemDescription' sx={{ wordBreak: 'break-all', ...descriptionSx }}>
                  {description}
                </Typography>
              ) : (
                description
              )}
            </Box>
          )}
        </Stack>
        <Box sx={{ width: actionRef?.current?.offsetWidth + isDownMd ? 12 : 16 }} />
        {secondaryAction !== undefined && (
          <ListItemSecondaryAction
            ref={actionRef}
            sx={
              isRtl
                ? { right: 'inherit', left: '0px', ...secondaryActionSx }
                : { right: '0px', left: 'inherit', ...secondaryActionSx }
            }
          >
            {secondaryAction}
          </ListItemSecondaryAction>
        )}
        {secondaryIcon !== undefined &&
          (typeof secondaryIcon === 'string' ? (
            <Box
              data-testid={AutomationTagIds['button.forwardarrow']}
              component='img'
              alt=''
              src={secondaryIcon}
              sx={{ width: '24px', height: '24px', transform: isRtl ? 'scaleX(-1)' : '', ...secondaryIconSx }}
            />
          ) : (
            secondaryIcon
          ))}
      </MuiListItem>
      {hasUnderDivider && (
        <Divider
          component='li'
          sx={{
            marginTop: '14px',
            marginBottom: '14px',
            marginLeft: !isRtl && icon !== undefined ? '64px' : '0px',
            marginRight: isRtl && icon !== undefined ? '64px' : '0px',
            ...dividerSx
          }}
        />
      )}
    </Stack>
  )
}

export default ListItem
